import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Button, Grid2, CircularProgress, AlertTitle, Alert } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MembershipSummary from './MembershipSummary';
import BenefitsList from './BenefitsList';
import { ContainerBox } from '../../../core/components/styled/StyledComponents';
import PaymentInformation, { PaymentFormHandles } from './stripe/PaymentInformation';
import PromoCodeInput from './PromoCodeInput';
import { useDispatch, useSelector } from 'react-redux';
import {
  SubscriptionPayment,
  getSelectedSubscription,
  setSubscriptionPayment,
} from '../../../core/store/slices/subscription.slice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiErrorLabel from '../../../core/components/ui/ApiErrorLabel';
import { PromoCodeData } from '../types/subscription';
import { usePaymentPolling } from '../hooks/usePaymentPolling';
import {
  ApplicationNote,
  ContentPaper,
  Divider,
  PageSubtitle,
  PageTitle,
  SectionTitle,
  SecureLabel,
  SecureText,
  SubmitButton,
  SummaryContainer,
} from './style';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { setMemberSubscription } from '../../../core/store/slices/memberSubscriptionSlice';
import { PaymentResult } from './stripe/PaymentInformationContent';
import toastService from '../../../services/toastService';
import { useAppSelector } from '../../../core/hooks/redux';
import { redeemPromoCode, validateCardPrice, ValidateCardResponse } from '../api/subscription.api';
import { RootState } from '../../../core/store';
import { useTrialEligibilityQuery } from '../hooks/useTrialEligibilityQuery';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedSubscription = useSelector(getSelectedSubscription);
  // const { hasApiError, getApiErrorBySource } = useApiErrors();

  const paymentFormRef = useRef<PaymentFormHandles>(null);
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [agreedToTerms, setAgreedToTerms] = useState(true); //set initial value false when consent button active
  const [paymentElementCompleted, setPaymentElementCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [promoData, setPromoData] = useState<PromoCodeData | null>(null);
  const [promoDiscount, setPromoDiscount] = useState<number>(0);

  const [stripeError, setStripeError] = useState('');
  const [paymentErrorType, setPaymentErrorType] = useState<string | null>(null);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [processingDuration, setProcessingDuration] = useState(0);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState<string | null>(null);
  const [subscriptionPayment, setLocalSubscriptionPayment] = useState<SubscriptionPayment | null>(
    null
  );
  const [searchParams] = useSearchParams();
  const isAddOn = Boolean(searchParams.get('addOn'));
  const basicInfo = useAppSelector((state: RootState) => state.basicInfo);

  // Check trial eligibility (only for new enrollments, not add-ons)
  const { data: trialEligibilityData, isLoading: isLoadingTrialEligibility } = useTrialEligibilityQuery(
    basicInfo._id,
    !isAddOn && !!basicInfo._id
  );
  const isTrialEligible = trialEligibilityData?.data?.trialEligible ?? false;

  const discount = 0;

  const initialDiscountAmount = selectedSubscription?.price! || 0;

  // State to track final price after all discounts (including promo codes)
  const [finalPrice, setFinalPrice] = useState<number>(initialDiscountAmount);
  const isSubscriptionAvailable = useAppSelector((state) => state.memberSubscription);
  const {
    isPolling,
    attempts: pollingAttempts,
    stopPolling,
  } = usePaymentPolling({
    isEnabled: isPaymentProcessing,
    maxAttempts: 5,
    baseDelay: 2000,
    onSuccess: (data: SubscriptionPayment) => {
      console.log('basicInfo', basicInfo); // Check if basicInfo object properties. 
      if (data) {
        setLocalSubscriptionPayment(data);
        dispatch(setSubscriptionPayment(data));
        dispatch(setMemberSubscription(data.subscription));
      }

      if (data.paymentStatus === 'succeeded') {
        if (processingTimerRef.current) {
          clearInterval(processingTimerRef.current);
          processingTimerRef.current = null;
        }
        setIsPaymentProcessing(false);
        toastService.success('Great news! Your payment was processed successfully.');

        // All eligible members redirected to addition-information to allow them to select schedule
        // Schedule selection is displayed for all eligible members after successful payment
        navigate('/addition-information', { replace: true });
      } else if (data.paymentStatus === 'failed' || data.paymentStatus === 'canceled') {
        if (processingTimerRef.current) {
          clearInterval(processingTimerRef.current);
          processingTimerRef.current = null;
        }
        setIsPaymentProcessing(false);
        setStripeError('Your payment was not successful. Please try again.');
        setPaymentErrorType('requires_payment_method');
      }
    },
    onError: (error) => {
      console.error('Error checking payment status:', error);
    },
    onMaxAttemptsReached: () => {
      // Max polling attempts reached
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
      setPaymentStatusMessage(
        "We couldn't confirm your payment status yet. If your payment was successful, you'll receive a confirmation email shortly."
      );
    },
  });

  const isPaymentReady = useMemo(
    () => !stripeError && paymentElementCompleted && !!selectedSubscription?.id,
    [stripeError, paymentElementCompleted, selectedSubscription?.id]
  );

  const handleStripePaymentElementChangeEvent = (
    paymentElementChanged: StripePaymentElementChangeEvent
  ) => {
    if (paymentElementChanged) {
      setPaymentElementCompleted(paymentElementChanged.complete);
    }
  };

  const handleSubscriptionPaymentData = (payment: SubscriptionPayment) => {
    setLocalSubscriptionPayment(payment);
    dispatch(setSubscriptionPayment(payment));
    dispatch(setMemberSubscription(payment.subscription));
  };

  // Update final price when initialDiscountAmount changes
  useEffect(() => {
    setFinalPrice(initialDiscountAmount);
  }, [initialDiscountAmount]);

  const handleApplyPromoCode = (code: string, data: PromoCodeData) => {
    setPromoCode(code);
    setPromoData(data);

    // Immediately pass the promo code down to the payment form so it's available
    // when the payment intent is created (avoid race where submit runs before
    // the promo code is forwarded).
    if (paymentFormRef.current && code) {
      paymentFormRef.current.setPromoCode?.(code);
    }

    // Calculate additional discount from promo code
    if (data.discountType === 'amount' && data.amountOff) {
      // For amount-based discounts - cap at product price
      const promoDiscountAmount = data.amountOff / 100; // Convert cents to dollars
      const cappedDiscount = Math.min(promoDiscountAmount, initialDiscountAmount);
      setPromoDiscount(cappedDiscount);
      setFinalPrice(Math.max(0, initialDiscountAmount - cappedDiscount));
    } else if (data.codeType === 'gift_card' && data.balanceCents) {
      // For gift cards - account for consumed amount in add-on scenarios
      const giftCardAmount = data.balanceCents / 100; // Convert cents to dollars
      const consumedAmount = isSubscriptionAvailable?.amount || 0;
      // Use remaining balance after accounting for what's been consumed
      const availableBalance = Math.max(0, giftCardAmount - consumedAmount);
      // Cap discount at product price
      const cappedDiscount = Math.min(availableBalance, initialDiscountAmount);
      setPromoDiscount(cappedDiscount);
      setFinalPrice(Math.max(0, initialDiscountAmount - cappedDiscount));
    }

    // Ask backend to compute price based on code and base amount
    const orderId = selectedSubscription?.id;
    validateCardPrice({
      code,
      amount: initialDiscountAmount,
      reason: orderId ? `Used with Order ${orderId}` : undefined,
      metadata: orderId ? { ExternalOrderId: String(orderId) } : undefined,
    })
      .then((response: { data: ValidateCardResponse }) => {
        const res = response.data;
        const serverPrice =
          typeof res?.data?.price === 'number'
            ? res.data.price
            : typeof res?.data?.priceCents === 'number'
              ? res.data.priceCents / 100
              : undefined;
        if (typeof serverPrice === 'number' && !Number.isNaN(serverPrice)) {
          // Account for consumed amount when calculating discount
          const consumedAmount = isSubscriptionAvailable?.amount || 0;
          const actualDiscount = Math.max(0, initialDiscountAmount - serverPrice);
          // For gift cards in add-on scenarios, ensure we don't exceed available balance
          const finalDiscount =
            data.codeType === 'gift_card' && isAddOn
              ? Math.min(actualDiscount, actualDiscount - consumedAmount)
              : actualDiscount;
          // Cap discount at product price
          const cappedDiscount = Math.min(finalDiscount, initialDiscountAmount);
          setPromoDiscount(cappedDiscount);
          setFinalPrice(Math.max(0, serverPrice));
        }
      })
      .catch((e: unknown) => {
        console.warn('validate-card failed; using local calculation', e);
      });
  };

  const handleClearPromoCode = () => {
    setPromoCode(null);
    setPromoData(null);
    setPromoDiscount(0);
    // Reset price to initial discount amount (without promo code)
    setFinalPrice(initialDiscountAmount);
  };

  const handleRetryPayment = () => {
    setStripeError('');
    setPaymentErrorType(null);
    if (paymentFormRef.current) {
      paymentFormRef.current.resetPaymentElement();
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (!paymentFormRef.current) {
        setStripeError('Payment system is not ready. Please refresh the page.');
        return;
      }

      // Ensure promo code is forwarded to the payment form BEFORE submitting
      // the payment so the backend receives it when creating the PaymentIntent.
      if (promoCode && paymentFormRef.current) {
        paymentFormRef.current.setPromoCode?.(promoCode);
      }

      const result: PaymentResult = await paymentFormRef.current.submitPayment();

      if (result?.success) {
        if (result?.subscriptionPayment) {
          handleSubscriptionPaymentData(result.subscriptionPayment);
        }
        if (result?.paymentIntent?.status === 'succeeded') {
          // If a promo code (e.g., gift card) was applied, redeem it via backend
          if (promoCode && promoDiscount > 0) {
            const orderId =
              result.subscriptionPayment?.subscription?._id ||
              result.subscriptionPayment?.subscription?.subscriptionId ||
              result.subscriptionPayment?.subscription?.memberId ||
              undefined;

            try {
              // Calculate the actual amount used for this transaction
              // For add-ons, only redeem what was actually applied (finalPrice vs initialDiscountAmount)
              const actualAmountUsed = Math.max(0, initialDiscountAmount - finalPrice);

              await redeemPromoCode({
                code: promoCode,
                amount: Number(actualAmountUsed),
                reason: orderId ? `Used with Order ${orderId}` : 'Used in subscription checkout',
                metadata: orderId ? { ExternalOrderId: String(orderId) } : undefined,
              });
            } catch (e) {
              // Do not block user flow on redeem failure
              console.error('Redeem promo code error:', e);
            }
          }
          toastService.success('Great news! Your payment was processed successfully.');

          if (isAddOn || (isSubscriptionAvailable && isSubscriptionAvailable.amount)) {
            navigate('/billing', { replace: true });
          } else {
            // Do NOT complete enrollment here — user must complete Schedule Selection (additional information) first.
            // Enrollment is completed when they finish the last step in addition-information (MindRightEnrollment2).
            // All eligible members are redirected to schedule selection after successful payment.
            navigate('/addition-information', { replace: true });
          }
        } else if (result?.paymentIntent?.status === 'processing') {
          setIsPaymentProcessing(true);
          setProcessingDuration(0);
          setPaymentStatusMessage(null);

          processingTimerRef.current = setInterval(() => {
            setProcessingDuration((prev) => prev + 1);
          }, 1000);
        }
      } else {
        if (result?.errorMessage) {
          setStripeError(result.errorMessage);
        }

        setPaymentErrorType(result?.errorType || null);
      }
    } catch (error: unknown) {
      console.error('Submission error:', error);

      const errorMessage = error?.toString() || '';
      if (errorMessage.includes('User is still in onboarding process')) {
        toastService.success('Great news! Your payment was processed successfully.');

        // Do NOT complete enrollment here — user must complete Schedule Selection first.
        // All eligible members are redirected to schedule selection after successful payment.
        navigate('/addition-information', { replace: true });
        return;
      }
      setStripeError('An unexpected error occurred during payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!selectedSubscription?.id) {
      navigate('/subscriptions');
    }
  }, [selectedSubscription, navigate]);

  useEffect(() => {
    return () => {
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
      stopPolling();
    };
  }, [stopPolling]);

  const renderProcessingState = () => (
    <Box sx={{ mt: 2, mb: 3 }} data-testid='payment-processing'>
      <Alert severity='info' icon={<AccessTimeIcon />}>
        <AlertTitle>Payment Processing</AlertTitle>
        Your payment is being processed. This may take a moment to complete. Please don't close this
        page.
        {subscriptionPayment && (
          <Typography variant='body2' sx={{ mt: 1 }}>
            Your subscription is being prepared and will be active soon.
          </Typography>
        )}
      </Alert>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'center' }}>
        {isPolling ? (
          <>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant='body2' color='text.secondary'>
              Processing for {processingDuration} seconds (Check {pollingAttempts}/5)
            </Typography>
          </>
        ) : (
          <Typography variant='body2' color='text.secondary' align='center'>
            {paymentStatusMessage ||
              "We're still processing your payment. You'll receive a confirmation email once completed."}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <ContainerBox
      sx={{
        padding: '0px',
        marginTop: 0,
        marginBottom: 0,
        '& > *:first-of-type': {
          marginTop: 0,
        },
      }}
    >
      <ContentPaper elevation={0}>
        <Grid2 container>
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Box
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                pt: { xs: 2, sm: 3, md: 3 },
                mt: 0,
              }}
              role='main'
              aria-labelledby='checkout-title'
            >
              {isAddOn ? (
                <PageTitle id='checkout-title'>MindRight Health</PageTitle>
              ) : (
                <PageTitle id='checkout-title'>Join MindRight Health</PageTitle>
              )}
              <PageSubtitle>Your journey to better mental health starts here</PageSubtitle>

              <SectionTitle id='promo-section'>Promo Code</SectionTitle>
              <div aria-labelledby='promo-section' style={{ marginBottom: '24px' }}>
                <PromoCodeInput
                  onApplyPromoCode={handleApplyPromoCode}
                  onClearPromoCode={handleClearPromoCode}
                />
              </div>

              <SectionTitle id='payment-section'>Payment Information</SectionTitle>

              <div aria-labelledby='payment-section'>
                <PaymentInformation
                  ref={paymentFormRef}
                  onPaymentElementChange={handleStripePaymentElementChangeEvent}
                />
              </div>

              <SecureLabel aria-label='Security information'>
                <LockIcon sx={{ fontSize: '1rem', mr: 0.5 }} aria-hidden='true' />
                <SecureText>Secure Payment</SecureText>
                <Divider>•</Divider>
                <SecureText>SSL Secure</SecureText>
                <Divider>•</Divider>
                <SecureText>PCI Compliant</SecureText>
              </SecureLabel>

              {stripeError && paymentErrorType !== 'requires_action' && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <ApiErrorLabel
                    message={stripeError}
                    variant='alert'
                    testId='stripe-error'
                    onDismiss={() => setStripeError('')}
                    dismissible={true}
                  />
                  {paymentErrorType === 'requires_payment_method' && (
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={handleRetryPayment}
                      sx={{ mt: 2 }}
                      startIcon={<RefreshIcon />}
                    >
                      Update Payment Details
                    </Button>
                  )}
                </Box>
              )}

              {paymentErrorType === 'requires_action' && (
                <Box sx={{ mt: 2, mb: 2 }} data-testid='authentication-notice'>
                  <Alert severity='info' icon={<LockIcon />}>
                    <AlertTitle>Authentication Required</AlertTitle>
                    You're being redirected to your bank's verification page. This helps keep your
                    payments secure.
                  </Alert>
                  <CircularProgress size={24} sx={{ mt: 2, display: 'block', mx: 'auto' }} />
                </Box>
              )}

              {isPaymentProcessing && renderProcessingState()}

              <SubmitButton
                onClick={handleSubmit}
                disabled={!agreedToTerms || isSubmitting || !isPaymentReady || isPaymentProcessing}
                disableElevation
                startIcon={isSubmitting ? <CircularProgress size={20} color='inherit' /> : null}
                aria-disabled={
                  !agreedToTerms || isSubmitting || !isPaymentReady || isPaymentProcessing
                }
                aria-busy={isSubmitting}
              >
                {isSubmitting
                  ? 'Processing...'
                  : isTrialEligible && !isAddOn
                    ? 'Start Free Trial'
                    : `Pay $${finalPrice.toFixed(2) || ''}`}
              </SubmitButton>
              {isAddOn ? (
                <ApplicationNote>Pay now and get additional moments instantly</ApplicationNote>
              ) : (
                <ApplicationNote>
                  We'll review your application within 3 business days
                </ApplicationNote>
              )}

            </Box>
          </Grid2>

          <Grid2
            size={{ xs: 12, md: 4 }}
            sx={{
              borderLeft: { xs: 'none', md: '1px solid #EEEDFF' },
              borderTop: { xs: '1px solid #EEEDFF', md: 'none' },
            }}
          >
            {selectedSubscription && (
              <SummaryContainer role='complementary' aria-label='Subscription summary'>
                <MembershipSummary
                  subscription={selectedSubscription}
                  discount={discount}
                  promoDiscount={promoDiscount}
                  promoCode={promoCode || undefined}
                  promoType={promoData?.codeType}
                  isTrialEligible={isTrialEligible}
                />
                <Box sx={{ mt: 4 }}>
                  <BenefitsList />
                </Box>
              </SummaryContainer>
            )}
          </Grid2>
        </Grid2>
      </ContentPaper>
    </ContainerBox>
  );
};

export default React.memo(CheckoutPage);
